/**
 * IT Admin → Learning → Access training progress (`/admin/access-training`,
 * 13 Oct; admins). Completion per role, then per roster group — site
 * owners (sites covered), HR validators (companies covered), approvers
 * (rules covered), each team — with who has and hasn't done it, the roster
 * corrections people sent (Accept / Dismiss; an approver's or, 14 Oct, an HR
 * validator's opens its rule in Approvers, prefilled; others say where the
 * change is made), and the Excel export that replaces the old mailto loop.
 */

import { CircleCheck, Download, Icon } from "@norican/icons";
import { Alert, Badge, Button, Card, Collapsible, StatisticBar, Typography } from "@norican/react";
import { useNavigate } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingState,
  PageContainer,
  SectionCard,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { ProgressGroup, RoleConfirmationView } from "@/lib/api";

import { ACCESS_TRAINING_EXPORT_URL, useResolveCorrection, useTrainingProgress } from "./api";

function GroupCard({ group }: { group: ProgressGroup }) {
  const { t } = useT();
  const missing = group.people.filter((p) => !p.completed);
  const done = group.people.filter((p) => p.completed);
  const label =
    group.kind === "team" ? group.label : t(`accessTraining.progress.kind.${group.kind}`);
  return (
    <Card variant="outline" padding="md" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Typography as="h3" variant="heading-6">
          {label}
        </Typography>
        <Badge
          tone={
            group.people_done === group.people_total && group.people_total > 0
              ? "success"
              : "neutral"
          }
          variant="subtle"
          size="sm"
        >
          {t("accessTraining.progress.people", {
            done: group.people_done,
            total: group.people_total,
          })}
        </Badge>
      </div>
      {group.kind !== "team" ? (
        <Typography as="p" variant="caption" tone="muted">
          {t(`accessTraining.progress.scopes.${group.kind}`, {
            done: group.scopes_done,
            total: group.scopes_total,
          })}
        </Typography>
      ) : null}
      <Collapsible.Root>
        <Collapsible.Trigger className="text-accent-fg-subtle self-start text-sm underline-offset-2 hover:underline">
          {t("accessTraining.progress.showPeople")}
        </Collapsible.Trigger>
        <Collapsible.Panel>
          <div className="grid gap-3 pt-2 sm:grid-cols-2">
            {[
              { title: t("accessTraining.progress.notDone"), people: missing },
              { title: t("accessTraining.progress.done"), people: done },
            ].map((col) => (
              <div key={col.title} className="flex flex-col gap-1">
                <Typography as="h4" variant="caption" className="font-medium">
                  {col.title} ({col.people.length})
                </Typography>
                <ul className="flex flex-col gap-1 text-sm">
                  {col.people.map((p) => (
                    <li key={`${p.rec_id ?? p.email ?? p.name}`} className="flex flex-col">
                      <span className="inline-flex items-center gap-1">
                        {p.completed ? <Icon icon={CircleCheck} size="xs" tone="success" /> : null}
                        {p.name}
                        {p.out_of_date ? (
                          <Badge tone="warning" variant="subtle" size="sm">
                            {t("accessTraining.chapter.outOfDate")}
                          </Badge>
                        ) : null}
                      </span>
                      {(p.scopes ?? []).length > 0 ? (
                        <span className="text-fg-muted text-xs" lang="en">
                          {(p.scopes ?? []).slice(0, 6).join(", ")}
                          {(p.scopes ?? []).length > 6 ? ` +${(p.scopes ?? []).length - 6}` : ""}
                        </span>
                      ) : null}
                      {p.corrected_to ? (
                        <span className="text-warning-fg text-xs">
                          {t("accessTraining.progress.correctedTo", { name: p.corrected_to })}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </Card>
  );
}

function CorrectionRow({ item }: { item: RoleConfirmationView }) {
  const { t } = useT();
  const navigate = useNavigate();
  const resolve = useResolveCorrection();
  const open = item.state === "open";
  return (
    <li className="border-border-subtle flex flex-col gap-1 border-b pb-3 last:border-b-0">
      <Typography as="p" variant="body-sm" className="font-medium" lang="en">
        {item.roster_label}
      </Typography>
      <Typography as="p" variant="body-sm">
        {t("accessTraining.progress.from", { name: item.user_name ?? item.user_email })}
        {" · "}
        {item.decision === "me"
          ? t("accessTraining.confirm.isMe")
          : t("accessTraining.progress.rightPerson", { name: item.suggested_person ?? "—" })}
      </Typography>
      {item.comment ? (
        <Typography as="p" variant="body-sm" tone="muted" className="whitespace-pre-wrap">
          “{item.comment}”
        </Typography>
      ) : null}
      {item.rule_id == null && item.apply_in ? (
        <Typography as="p" variant="caption" tone="muted">
          {t("accessTraining.progress.applyIn")}{" "}
          <span lang="en" className="font-mono">
            {item.apply_in}
          </span>
        </Typography>
      ) : null}
      {open ? (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            loading={resolve.isPending}
            onClick={() =>
              resolve.mutate(
                { id: item.id, accept: true },
                {
                  onSuccess: (result) => {
                    if (result.open_url) {
                      void navigate(result.open_url);
                    }
                  },
                },
              )
            }
          >
            {item.rule_id != null
              ? t("accessTraining.progress.acceptOpen")
              : t("accessTraining.progress.accept")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ id: item.id, accept: false })}
          >
            {t("accessTraining.progress.dismiss")}
          </Button>
        </div>
      ) : (
        <Badge tone={item.state === "accepted" ? "success" : "neutral"} variant="subtle" size="sm">
          {t(
            `accessTraining.progress.state.${item.state === "accepted" ? "accepted" : "dismissed"}`,
          )}
        </Badge>
      )}
      {resolve.isError ? <ErrorState error={resolve.error} variant="inline" /> : null}
    </li>
  );
}

export function AccessTrainingProgressPage() {
  const { t } = useT();
  useDocumentTitle(t("accessTraining.progress.title"));
  const progress = useTrainingProgress();
  const breadcrumbs = <AppBreadcrumbs items={[{ label: t("accessTraining.progress.title") }]} />;

  return (
    <PageContainer
      title={t("accessTraining.progress.title")}
      subtitle={t("accessTraining.progress.subtitle")}
      breadcrumbs={breadcrumbs}
      width="wide"
      actions={
        <Button
          variant="outline"
          startIcon={<Icon icon={Download} size="sm" />}
          render={<a href={ACCESS_TRAINING_EXPORT_URL} download />}
        >
          {t("accessTraining.progress.export")}
        </Button>
      }
    >
      {progress.isPending ? (
        <LoadingState />
      ) : progress.isError ? (
        <ErrorState error={progress.error} onRetry={() => void progress.refetch()} />
      ) : (
        <div className="flex flex-col gap-6">
          {progress.data.partial ? (
            <Alert tone="warning">{t("accessTraining.progress.partial")}</Alert>
          ) : null}
          <Typography as="p" variant="caption" tone="muted">
            {t("accessTraining.progress.version", {
              version: progress.data.version,
              gate: progress.data.gate,
            })}
          </Typography>
          <StatisticBar
            aria-label={t("accessTraining.progress.byRole")}
            items={progress.data.roles.map((r) => ({
              label: t(`accessTraining.role.${r.role}`),
              value:
                r.population != null ? `${r.completed} / ${r.population}` : String(r.completed),
            }))}
          />
          <section className="flex flex-col gap-3">
            <Typography as="h2" variant="heading-5">
              {t("accessTraining.progress.groups")}
            </Typography>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {progress.data.groups.map((group) => (
                <GroupCard key={group.id} group={group} />
              ))}
            </div>
          </section>
          <SectionCard title={t("accessTraining.progress.corrections")}>
            {progress.data.corrections.length === 0 ? (
              <Typography as="p" variant="body-sm" tone="muted">
                {t("accessTraining.progress.noCorrections")}
              </Typography>
            ) : (
              <ul className="flex flex-col gap-3">
                {progress.data.corrections.map((item) => (
                  <CorrectionRow key={item.id} item={item} />
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      )}
    </PageContainer>
  );
}
